import type { ReactNode } from "react";
import {
  Activity,
  BellRing,
  BookOpen,
  Braces,
  Code,
  Globe,
  KeyRound,
  LifeBuoy,
  PanelsTopLeft,
  Plug,
  Rocket,
  Server,
  ShieldCheck,
  Siren,
  Sparkles,
  Users,
  type LucideProps,
} from "lucide-react";
import { Link } from "zudoku/router";

const icons: Record<string, React.ComponentType<LucideProps>> = {
  activity: Activity,
  bell: BellRing,
  book: BookOpen,
  braces: Braces,
  code: Code,
  globe: Globe,
  key: KeyRound,
  "life-buoy": LifeBuoy,
  "status-page": PanelsTopLeft,
  plug: Plug,
  rocket: Rocket,
  server: Server,
  shield: ShieldCheck,
  siren: Siren,
  sparkles: Sparkles,
  users: Users,
};

type CardProps = {
  title: string;
  icon?: keyof typeof icons;
  href?: string;
  children?: ReactNode;
};

/**
 * A link tile in a hairline grid (the dashboard's bento cells). The whole tile
 * is the link; the icon is decorative. Styled by the `sp-card*` classes in
 * `src/theme.ts` (Zudoku's Tailwind build doesn't see this folder).
 */
export function Card({ title, icon, href, children }: CardProps) {
  const Icon = icon ? icons[icon] : undefined;
  const external = href?.startsWith("http") || href?.startsWith("mailto:");
  const linkClass = "sp-card-link";

  return (
    <div className="not-prose sp-card">
      {Icon && <Icon aria-hidden="true" strokeWidth={1.75} className="sp-card-icon" />}
      <p className="sp-card-title">
        {href ? (
          external ? (
            <a href={href} className={linkClass} rel="noopener noreferrer">
              {title}
            </a>
          ) : (
            <Link to={href} className={linkClass}>
              {title}
            </Link>
          )
        ) : (
          title
        )}
      </p>
      {children && <div className="sp-card-body">{children}</div>}
    </div>
  );
}

/**
 * Cards in a grid whose cells share 1px borders.
 */
export function CardGroup({ cols = 2, children }: { cols?: number; children: ReactNode }) {
  const columns = Math.max(1, Math.min(cols, 3));

  return (
    <div
      className="not-prose sp-card-grid"
      style={{
        gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${columns === 3 ? "12rem" : "15rem"}), 1fr))`,
      }}
    >
      {children}
    </div>
  );
}
